import { useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthSideImage } from "@/components/AuthSideImage";
import { useSite } from "@/context/SiteContext";

export default function Login() {
  const { login, formatApiErrorDetail } = useAuth();
  const { config, t } = useSite();
  const ob = config?.onboarding || {};
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name?.split(" ")[0] || "chef"}!`);
      navigate(u.is_admin ? "/admin" : "/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#fbf9f7]" data-testid="login-page">
      <AuthSideImage image={ob.login_image} text={ob.login_side_text} t={t} />

      <div className="flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-[#d00000] transition-colors mb-10" data-testid="back-home">
            <ArrowLeft className="w-4 h-4" /> {t("Back to Jollanda")}
          </Link>
          <div className="mb-8">
            <BrandLogo className="h-14" testId="login-logo-link" />
          </div>
          <h1 className="font-display text-4xl font-black text-neutral-900" data-testid="login-title">{t(ob.login_title || "Welcome back.")}</h1>
          <p className="text-neutral-500 mt-2">{t(ob.login_subtitle || "")}</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Email</Label>
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email"
                className="mt-2 h-12 bg-white border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            <div>
              <Label className="text-neutral-500 text-xs uppercase tracking-widest">Password</Label>
              <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="login-password"
                className="mt-2 h-12 bg-white border-neutral-300 text-neutral-900 rounded-none focus-visible:ring-[#d00000]" />
            </div>
            {error && <p className="text-sm text-[#d00000]" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-12 bg-[#d00000] hover:bg-[#a30000] text-white rounded-none">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-neutral-500">
            New to Jollanda? <Link to="/register" className="text-neutral-900 underline hover:text-[#d00000]" data-testid="go-register">Create an account</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
